import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('elevate360_token');

    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        localStorage.setItem('elevate360_user', JSON.stringify(response.data));
      } catch (err) {
        localStorage.removeItem('elevate360_token');
        localStorage.removeItem('elevate360_user');
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('elevate360_token', access_token);
      localStorage.setItem('elevate360_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      throw err;
    }
  };

  const register = async (email, password, name, referralCode = null) => {
    setError(null);
    try {
      const refCode = referralCode || localStorage.getItem('referral_code');
      const response = await authAPI.register({ email, password, name, referral_code: refCode });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('elevate360_token', access_token);
      localStorage.setItem('elevate360_user', JSON.stringify(userData));
      localStorage.removeItem('referral_code');
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      throw err;
    }
  };

  const sendOtp = async (email, password, name, phone = '') => {
    setError(null);
    const refCode = localStorage.getItem('referral_code');
    const response = await authAPI.sendOtp({ email, password, name, phone, referral_code: refCode });
    return response.data; // { message: "..." }
  };

  const verifyOtp = async (email, otp) => {
    setError(null);
    try {
      const response = await authAPI.verifyOtp({ email, otp });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('elevate360_token', access_token);
      localStorage.setItem('elevate360_user', JSON.stringify(userData));
      localStorage.removeItem('referral_code');
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
      throw err;
    }
  };

  const processOAuthSession = async (sessionId) => {
    setError(null);
    try {
      const response = await authAPI.processOAuthSession(sessionId);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('elevate360_token', access_token);
      localStorage.setItem('elevate360_user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.response?.data?.detail || 'OAuth login failed');
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem('elevate360_token');
    localStorage.removeItem('elevate360_user');
    setUser(null);
  };

  const isAdmin = () => {
    return user && ['super_admin', 'instructor', 'reviewer', 'developer', 'course_coordinator'].includes(user.role);
  };

  const isSuperAdmin = () => {
    return user && user.role === 'super_admin';
  };

  const isMentor = () => {
    return user && user.role === 'mentor';
  };

  const isDelegate = () => {
    return user && user.role === 'delegate';
  };

  const isDeveloper = () => {
    return user && user.role === 'developer';
  };

  const isCourseCoordinator = () => {
    return user && user.role === 'course_coordinator';
  };

  const isSEO = () => {
    return user && user.role === 'seo';
  };

  // Anyone allowed to manage Blogs: full admins OR the dedicated SEO role.
  const canManageBlogs = () => isAdmin() || isSEO();

  // Public website content (Events, Gallery, Website Settings): same people as Blogs.
  const canManageSiteContent = () => isAdmin() || isSEO();

  const value = {
    user,
    loading,
    error,
    login,
    register,
    sendOtp,
    verifyOtp,
    logout,
    processOAuthSession,
    isAdmin,
    isSuperAdmin,
    isMentor,
    isDelegate,
    isDeveloper,
    isCourseCoordinator,
    isSEO,
    canManageBlogs,
    canManageSiteContent,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
